import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import SvgIcon from '@home/components/SvgIcon';
import BasicImage from '@home/components/BasicImage';
import classNames from 'classnames';
import moment from 'moment';
import { concat, isEmpty, last } from 'lodash-es';
import {
  useIntl,
  useHistory,
  useLocation,
  shallowEqual,
  useSelector,
} from 'umi';
import { NavLink } from 'react-router-dom';
import { useDebounceFn } from 'ahooks';
import { Bubble, Modal, SearchBox } from 'tea-component';
import { v4 as uuid } from 'uuid';
import Tabs from '../Tabs';
import ChartShareModal from '@/pages/aigc/pages/Agent/ChatShare/ChatShareModal';
import CommonBubble from '@home/components/CommonBubble';
import { ChatShareDialog } from '@/pages/aigc/pages/Agent/ChatShare/types/chatShare';
import { storage } from '@/utils/utils';
import { useErrorTips } from '@/pages/aigc/hooks/useErrorTips';
import {
  getSearchSessionQuery,
  SearchSessionQueryResponse,
  updateChat,
} from '../../service/service';
import { SKILL_AGENT_UUID } from '../../ChatPage/utils/submitHelpers';
import { ChatList, useChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import {
  useAgentStoreContext,
  useLocalState,
  store,
} from '../../ChatPage/hooks/useLocalState';
import {
  COPILOT_REPORT_SESSION_ID_KEY,
  DATABRAIN_AGENT_ID2,
} from '../../config/constant';
import DataBrainLogo from '@/pages/home/assets/img/ai-text.svg';
import CopilotBackColor from '@/pages/aigc/assets/img/copilot_back_color.png';
import AgentCollectionIcon from '../../svg/agent-mdel-collection-icon-menu.svg';
import AgentCollectionDefaultIcon from '@/pages/aigc/assets/svg/agent-collection-default-icon.svg';
import SkillsCollectionIcon from '@/pages/aigc/assets/svg/icon-skills-collection.svg';
import SkillsCollectionDefaultIcon from '@/pages/aigc/assets/svg/skills-collection-default-icon.svg';
import FileLibraryIcon from '@/pages/aigc/assets/svg/icon-file-library.svg';
import CollectStarIcon from '@/pages/aigc/assets/svg/collect-star.svg';
import ArrowIcon from '@/pages/aigc/assets/svg/icon-arrow.svg';
import InfoIcon from '@/pages/aigc/assets/svg/icon-info.svg';
import ChatNewIcon from '@/pages/aigc/assets/svg/icon-chat-new.svg';
import ExtractIcon from '@/pages/aigc/assets/svg/icon-extract.svg';
import { useCopilotOpenBridge } from '@/pages/aigc/pages/Agent/Copilot/hooks/useCopilotOpenBridge';

import './index.scss';
import { OrganizationText } from '@/pages/aigc/components/OrganizationPermission';
import { useGlobalReportManager } from '../ReportViewer/useGlobalReportManager';

type SearchSessionQueryResponseInChats = SearchSessionQueryResponse['chats'];

const RouterNavLink = NavLink as any;

type HeaderMenuProps = {
  isNormal: boolean;
  onSet: (args?: HeaderMenuProps['isNormal']) => void;
  searchChatData: SearchSessionQueryResponseInChats;
  showNoDataInReturn: boolean;
};

type UpdateChatTypes = {
  sessionId: string;
  title: string;
  isDelete?: boolean;
  isBookmarked?: boolean;
  callBack?: () => void;
};

const MainMenu = (props: Partial<HeaderMenuProps>) => {
  const { isNormal, searchChatData, showNoDataInReturn } = props;
  // 使用
  const { getItem } = useAgentStoreContext();
  const {
    fetchChatList,
    loading: chatListLoading,
    chatList,
    bookMarkedChats,
  } = useChatList();
  const showBookmarkList = useMemo(
    () =>
      searchChatData !== undefined && searchChatData.length > 0
        ? searchChatData.filter((searchChat) =>
          bookMarkedChats.some(
            (bookmarkChat) =>
              searchChat.session_id === bookmarkChat.session_id,
          ),
        )
        : bookMarkedChats,
    [bookMarkedChats, searchChatData],
  );

  const userInfo = useSelector(
    (state: any) => state?.home?.userInfo,
    shallowEqual,
  );
  const history = useHistory();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const showErrorTips = useErrorTips();
  const { sessionId } = location?.query || ({} as any);
  const pendingSessions = useRef<string[]>([]);
  const [bubbleId, setBubbleId] = useState('');
  const isBubbleHoveredRef = useRef(false);
  const [editingId, setEditingId] = useState<string | null>(null); // 当前正在编辑的会话ID
  const [isEditing, setIsEditing] = useState(false);
  const halfYearAgo = new Date();

  const initialExpandBookmark = storage.getItem('__dba_bookmark_expand');
  const [isBookmarkExpanded, setIsBookmarkExpanded] = useState(
    initialExpandBookmark === 'show',
  );
  const bookmarkTitleRef = useRef<HTMLDivElement>(null);
  const bookmarkDivRef = useRef<HTMLDivElement>(null);
  const chatsDivRef = useRef<HTMLDivElement>(null);

  const streamingState = getItem(
    `__dba_chat_history_streaming_state_${sessionId}__`,
  );
  // 分享弹窗
  const [showShareChatDialog, setShowShareChatDialog] =
    useState<ChatShareDialog>();

  const onMouseEnter = (id: string) => {
    if (id === bubbleId) return;
    // 鼠标移向 bubble 时不清除 bubbleId，由 bubble 的 onVisibleChange 负责关闭
    if (!id && isBubbleHoveredRef.current) return;
    setBubbleId(id);
  };
  halfYearAgo.setMonth(halfYearAgo.getMonth() - 6);

  /**
   * 判断是否为历史会话（非当前活跃会话）
   * @param streamingState - 流式状态对象
   * @returns 是否为历史会话
   */
  const isHistoricalSession = (streamingState: any): boolean =>
    !isEmpty(streamingState) && !streamingState?.current;

  const classifyByTime = useCallback(() => {
    const todayItems: ChatList[] = []; // 今日（00:00-24:00）
    const thisWeekItems: ChatList[] = []; // 近7天（昨天到6天前）
    const thisMonthItems: ChatList[] = []; // 近30天（7天前到30天前）
    const earlierItems: ChatList[] = []; // 更早（30天前）

    const now = moment();
    const todayStart = now.clone().startOf('day');
    const todayEnd = todayStart.clone().add(1, 'day');

    const past7DaysStart = todayStart.clone().subtract(6, 'days');
    const past7DaysEnd = todayStart.clone();

    const past30DaysStart = todayStart.clone().subtract(30, 'days');
    const past30DaysEnd = past7DaysStart.clone();

    (searchChatData && searchChatData.length > 0
      ? searchChatData
      : chatList
    ).forEach((item) => {
      // 将数据时间视为UTC+8，然后转换为本地时间
      const itemDate =
        typeof item.timestamp === 'string'
          ? moment
            .parseZone(`${item.timestamp.replace(/-/g, '/')}+08:00`)
            .local()
          : moment.parseZone(`${item.timestamp}+08:00`).local();

      const checkedIsBookmarkedItem: ChatList = bookMarkedChats.some(
        (chat) => chat.session_id === item.session_id,
      )
        ? { ...item, bookmarked: true }
        : item;

      if (
        itemDate.isSameOrAfter(todayStart.clone()) &&
        itemDate.isBefore(todayEnd.clone())
      ) {
        todayItems.push(checkedIsBookmarkedItem);
      } else if (
        itemDate.isSameOrAfter(past7DaysStart.clone()) &&
        itemDate.isBefore(past7DaysEnd.clone())
      ) {
        thisWeekItems.push(checkedIsBookmarkedItem);
      } else if (
        itemDate.isSameOrAfter(past30DaysStart.clone()) &&
        itemDate.isBefore(past30DaysEnd.clone())
      ) {
        thisMonthItems.push(checkedIsBookmarkedItem);
      } else {
        earlierItems.push(checkedIsBookmarkedItem);
      }
    });

    return {
      todayItems,
      thisWeekItems,
      thisMonthItems,
      earlierItems,
    };
  }, [bookMarkedChats, chatList, searchChatData]);

  const { todayItems, thisWeekItems, thisMonthItems, earlierItems } =
    classifyByTime();

  const fetchUpdateChat = async ({
    sessionId,
    title,
    isDelete,
    isBookmarked,
    callBack,
  }: UpdateChatTypes) => {
    try {
      const { openid } = userInfo || {};
      if (!openid) return;

      !isDelete && !isBookmarked && setIsEditing(true);
      setEditingId(sessionId);

      await updateChat(
        {
          sessionId,
          title,
          isDelete,
          isBookmarked,
          openid,
        },
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        showErrorTips,
      );
      fetchChatList();
    } catch (err) {
      callBack?.();
    } finally {
      setIsEditing(false);
      setEditingId(null);
    }
  };

  const handleEdit = (
    id: string,
    newTitle: string,
    callBack: () => void,
    isBookmarked: boolean,
  ) => {
    fetchUpdateChat({
      sessionId: id,
      title: newTitle,
      isDelete: false,
      callBack,
      isBookmarked,
    });
  };

  const handleBookmarkChat = (id: string, isRemoveArchive: boolean) => {
    const chatTitle =
      chatList.find((chat) => chat.session_id === id)?.title ?? '';
    const bookmarkChatTitle =
      showBookmarkList.find((chat) => chat.session_id === id)?.title ?? '';

    if (isRemoveArchive)
      fetchUpdateChat({
        sessionId: id,
        title: bookmarkChatTitle,
        isBookmarked: false,
      });
    else
      fetchUpdateChat({ sessionId: id, title: chatTitle, isBookmarked: true });
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      message: formatMessage({ id: 'dba_menu_confirm' }),
      description: formatMessage({ id: 'dba_menu_confirm_content' }),
      okText: formatMessage({ id: 'aigc_common_confirm' }),
      cancelText: formatMessage({ id: 'aigc_common_cancel' }),
      className: 'dba-agent-delete-modal',
      onOk: () => {
        fetchUpdateChat({
          sessionId: id,
          title: '',
          isDelete: true,
          isBookmarked: false,
        });
        if (sessionId !== id) {
          return;
        }
        const findChat = chatList.find((item) => item.session_id !== id);
        if (findChat) {
          history.push({
            pathname: '/agent/chat',
            query: { sessionId: findChat.session_id },
          });
        } else {
          history.push('/agent/home');
        }
      },
    });
  };

  const isNewChat = useMemo(() => {
    if (!sessionId) return false;

    const existsInChatList = chatList?.some(
      (item) => item?.session_id === sessionId,
    );
    const existsInBookmarkChatList = showBookmarkList?.some(
      (item) => item?.session_id === sessionId,
    );
    const existsInSearchChatList = searchChatData?.some(
      (item) => item?.session_id === sessionId,
    );
    const existsInPending = pendingSessions.current.includes(sessionId);
    if (isHistoricalSession(streamingState)) {
      pendingSessions.current = pendingSessions.current.filter(
        (id) => id !== sessionId,
      );
      return false;
    }
    // 如果在 chatList 里已存在，则从 pending 列表移除
    if (existsInChatList && existsInPending) {
      pendingSessions.current = pendingSessions.current.filter(
        (id) => id !== sessionId,
      );
      return false;
    }

    // 如果在 bookmarkchatList 里已存在，则从 pending 列表移除
    if (existsInBookmarkChatList && existsInPending) {
      pendingSessions.current = pendingSessions.current.filter(
        (id) => id !== sessionId,
      );
      return false;
    }
    // 如果在 searchchatList 里已存在，则从 pending 列表移除
    if (existsInSearchChatList && existsInPending) {
      pendingSessions.current = pendingSessions.current.filter(
        (id) => id !== sessionId,
      );
      return false;
    }

    // 如果既不在 chatList 也不在 pending 列表，则添加到 pending
    if (
      !existsInChatList &&
      !existsInPending &&
      !existsInBookmarkChatList &&
      !existsInSearchChatList
    ) {
      pendingSessions.current = [sessionId, ...pendingSessions.current];
    }

    return (
      !existsInChatList && !existsInBookmarkChatList && !existsInSearchChatList
    );
  }, [sessionId, chatList, showBookmarkList, searchChatData]);

  const newSessionsToShow = useMemo(() => {
    // 构建需要显示的会话列表，保持原有顺序并去重
    const sessionsToShow: string[] = [];
    // 0、历史数据不需要展示新对话
    if (isHistoricalSession(streamingState)) {
      return [];
    }
    // 判断，如果当前前端维护的pendingSessions列表已经在接口返回，需要过滤，防止弱网情况下，新对话展示异常
    if (pendingSessions.current && pendingSessions.current.length > 0) {
      pendingSessions.current = pendingSessions.current.filter(
        (pendingId) =>
          !chatList.some((chat) => chat.session_id === pendingId) &&
          !showBookmarkList.some((chat) => chat.session_id === pendingId),
      );
    }

    // 1. 如果 sessionId 不在任何现有列表中，则放在第一位
    if (
      sessionId &&
      ![
        ...(pendingSessions.current || []),
        ...chatList,
        ...showBookmarkList,
        ...(searchChatData ?? []),
      ]?.some((item) =>
        typeof item === 'string'
          ? item === sessionId
          : item?.session_id === sessionId,
      )
    ) {
      sessionsToShow.push(sessionId);
    }

    // 2. 添加 pendingSessions 中的会话，保持原有顺序
    if (pendingSessions.current) {
      pendingSessions.current.forEach((sessionId) => {
        if (!sessionsToShow.includes(sessionId)) {
          sessionsToShow.push(sessionId);
        }
      });
    }

    return sessionsToShow;
  }, [sessionId, chatList, showBookmarkList, searchChatData]);

  // 计算出当前显示的 item
  const { referenceElement, visible, targetItem } = useMemo(() => {
    if (!bubbleId)
      return { referenceElement: null, visible: false, targetItem: null };
    const referenceElement = document.getElementById(bubbleId);
    if (!referenceElement)
      return { referenceElement: null, visible: false, targetItem: null };
    // 查找对应的 item 数据
    let targetItem = null;
    // 在所有分类中查找匹配的 item
    const allItems = [
      ...todayItems,
      ...thisWeekItems,
      ...thisMonthItems,
      ...earlierItems,
      ...bookMarkedChats,
    ];
    targetItem = allItems.find(
      (item) => item.session_id === bubbleId?.replace('bookmark-', ''),
    )?.title;
    return { referenceElement, visible: bubbleId, targetItem };
  }, [
    bubbleId,
    earlierItems,
    thisMonthItems,
    thisWeekItems,
    todayItems,
    isEditing,
    bookMarkedChats,
  ]);

  const handleBookmarkExpand = (isExpand: boolean) => {
    const bookmarkTitleRect = bookmarkTitleRef.current?.getBoundingClientRect();
    if (!bookmarkTitleRect || !bookmarkDivRef.current) return;

    bookmarkDivRef.current.style.height = '';
    setIsBookmarkExpanded(isExpand);
    storage.setItem('__dba_bookmark_expand', isExpand ? 'show' : 'hidden');
    return;
  };

  if (showNoDataInReturn)
    return (
      <div className={'dba-no-content'}>
        {formatMessage({ id: 'dba_no_search_result' })}
      </div>
    );

  const isFileLibraryActive = location.pathname?.includes('/agent/files');

  return (
    isNormal && (
      <section ref={chatsDivRef} className="dba-leftmenu navmenu-bd">
        <div
          className={classNames('dba-file-library-entry', {
            'dba-file-library-entry--active': isFileLibraryActive,
          })}
          onClick={() => history.push('/agent/files')}
        >
          <span>
            <BasicImage
              src={FileLibraryIcon}
              alt=""
              className="dba-file-library-entry__icon"
            />
            {formatMessage({ id: 'dba_file_library_title' })}
          </span>
        </div>
        <section
          ref={bookmarkDivRef}
          className={'dba-bookmark-container'}
          data-is-expanded={isBookmarkExpanded}
          data-min-content-height={showBookmarkList.length < 4}
        >
          <div
            ref={bookmarkTitleRef}
            className={'dba-bookmark-title'}
            data-is-expanded={isBookmarkExpanded}
            onClick={() => handleBookmarkExpand(!isBookmarkExpanded)}
          >
            <span>
              <BasicImage
                src={CollectStarIcon}
                alt=""
                className="dba-bookmark-title-collect-icon"
              />
              {formatMessage({ id: 'dba_archive_title' })}
            </span>

            <SvgIcon
              type={isBookmarkExpanded ? 'arrow-up' : 'arrow-down'}
              onClick={() => handleBookmarkExpand(!isBookmarkExpanded)}
            />
          </div>

          <div className="dba-menu-content-wrapper">
            {isBookmarkExpanded &&
              showBookmarkList.map((item) => (
                <Tabs
                  setShowShareChatDialog={setShowShareChatDialog}
                  hasSensitiveContent={item.has_sensitive_data}
                  active={sessionId === item.session_id}
                  key={item.session_id}
                  title={item.title}
                  id={item.session_id}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isLoading={editingId === item.session_id && isEditing}
                  onMouseEnter={onMouseEnter}
                  onBookmark={handleBookmarkChat}
                  isBookmarkItem={true}
                  isInBookmark
                  searchQuery={{
                    searchKeyword: (
                      item as unknown as SearchSessionQueryResponseInChats[0]
                    ).keyword,
                    searchQuery: (
                      item as unknown as SearchSessionQueryResponseInChats[0]
                    ).messages,
                  }}
                  operation={searchChatData === undefined}
                />
              ))}
          </div>
        </section>

        <section className="dba-menu-content-wrapper">
          {/* 今日 */}
          {(isNewChat ||
            todayItems.length > 0 ||
            newSessionsToShow.length > 0) && (
            <div className={classNames('dba-today', 'dba-time-section')}>
              <div className="dba-small-title">
                {formatMessage({ id: 'dba_menu_today' })}
              </div>
              {!chatListLoading &&
                searchChatData === undefined &&
                newSessionsToShow
                  .filter(
                    (val) => !chatList.some((item) => item.session_id === val),
                  )
                  .map((val) => (
                    <Tabs
                      setShowShareChatDialog={setShowShareChatDialog}
                      hasSensitiveContent={val.has_sensitive_data}
                      active={sessionId === val}
                      key={val}
                      id={val}
                      title={formatMessage({ id: 'dba_chat_new_menu' })}
                      operation={false}
                      onMouseEnter={onMouseEnter}
                    />
                  ))}
              {todayItems.map((item) => (
                <Tabs
                  setShowShareChatDialog={setShowShareChatDialog}
                  hasSensitiveContent={item.has_sensitive_data}
                  active={sessionId === item.session_id}
                  key={item.session_id}
                  title={item.title}
                  id={item.session_id}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isLoading={editingId === item.session_id && isEditing}
                  onMouseEnter={onMouseEnter}
                  onBookmark={handleBookmarkChat}
                  isBookmarkItem={bookMarkedChats.some(
                    (chat) => chat.session_id === item.session_id,
                  )}
                  searchQuery={{
                    searchKeyword: (
                      item as unknown as SearchSessionQueryResponseInChats[0]
                    ).keyword,
                    searchQuery: (
                      item as unknown as SearchSessionQueryResponseInChats[0]
                    ).messages,
                  }}
                  operation={searchChatData === undefined}
                />
              ))}
            </div>
          )}

          {/* 本周 */}
          {thisWeekItems.length > 0 && (
            <div className={classNames('dba-this-week', 'dba-time-section')}>
              <div className="dba-small-title">
                {formatMessage({ id: 'dba_menu_week' })}
              </div>
              {thisWeekItems.map((item) => (
                <Tabs
                  setShowShareChatDialog={setShowShareChatDialog}
                  hasSensitiveContent={item.has_sensitive_data}
                  active={sessionId === item.session_id}
                  key={item.session_id}
                  title={item.title}
                  id={item.session_id}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isLoading={editingId === item.session_id && isEditing}
                  onMouseEnter={onMouseEnter}
                  onBookmark={handleBookmarkChat}
                  isBookmarkItem={bookMarkedChats.some(
                    (chat) => chat.session_id === item.session_id,
                  )}
                  searchQuery={{
                    searchKeyword: (
                      item as unknown as SearchSessionQueryResponseInChats[0]
                    ).keyword,
                    searchQuery: (
                      item as unknown as SearchSessionQueryResponseInChats[0]
                    ).messages,
                  }}
                  operation={searchChatData === undefined}
                />
              ))}
            </div>
          )}

          {/* 本月 */}
          {thisMonthItems.length > 0 && (
            <div className={classNames('dba-this-month', 'dba-time-section')}>
              <div className="dba-small-title">
                {formatMessage({ id: 'dba_menu_month' })}
              </div>
              {thisMonthItems.map((item) => (
                <Tabs
                  setShowShareChatDialog={setShowShareChatDialog}
                  hasSensitiveContent={item.has_sensitive_data}
                  active={sessionId === item.session_id}
                  key={item.session_id}
                  title={item.title}
                  id={item.session_id}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isLoading={editingId === item.session_id && isEditing}
                  onMouseEnter={onMouseEnter}
                  onBookmark={handleBookmarkChat}
                  isBookmarkItem={bookMarkedChats.some(
                    (chat) => chat.session_id === item.session_id,
                  )}
                  searchQuery={{
                    searchKeyword: (
                      item as unknown as SearchSessionQueryResponseInChats[0]
                    ).keyword,
                    searchQuery: (
                      item as unknown as SearchSessionQueryResponseInChats[0]
                    ).messages,
                  }}
                  operation={searchChatData === undefined}
                />
              ))}
            </div>
          )}

          {/* 更早 */}
          {earlierItems.length > 0 && (
            <div className={classNames('dba-earlier', 'dba-time-section')}>
              <div className="dba-small-title">
                {formatMessage({ id: 'dba_menu_early' })}
              </div>
              {earlierItems.map((item) => (
                <Tabs
                  setShowShareChatDialog={setShowShareChatDialog}
                  hasSensitiveContent={item.has_sensitive_data}
                  active={sessionId === item.session_id}
                  key={item.session_id}
                  title={item.title}
                  id={item.session_id}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isLoading={editingId === item.session_id && isEditing}
                  onMouseEnter={onMouseEnter}
                  onBookmark={handleBookmarkChat}
                  isBookmarkItem={bookMarkedChats.some(
                    (chat) => chat.session_id === item.session_id,
                  )}
                  searchQuery={{
                    searchKeyword: (
                      item as unknown as SearchSessionQueryResponseInChats[0]
                    ).keyword,
                    searchQuery: (
                      item as unknown as SearchSessionQueryResponseInChats[0]
                    ).messages,
                  }}
                  operation={searchChatData === undefined}
                />
              ))}
            </div>
          )}
        </section>

        <CommonBubble
          content={targetItem}
          closeOnScroll={true}
          destroyOnClose={true}
          visible={visible}
          referenceElement={referenceElement}
          overlayClassName="dba-switch-agent-accordion-bubble dba-qa-tab-title-bubble dba-nav-menu-overlay"
          placement="right"
          className="tea-bubble--reverse"
          escapeWithReference
          flipped={false}
          onVisibleChange={(v) => {
            isBubbleHoveredRef.current = v;
            if (!v && bubbleId) {
              setBubbleId('');
            }
          }}
        />

        {showShareChatDialog?.isShowCopyDialog && (
          <ChartShareModal
            showShareChatDialog={showShareChatDialog}
            setShowShareChatDialog={setShowShareChatDialog}
          />
        )}
      </section>
    )
  );
};

export const HeaderMenu = (props: any) => {
  const {
    isNormal,
    onSet,
    setInitialTransitionDisabled,
    setSearchChatData,
    setShowNoDataInReturn,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;
  const { sessionId, template, tab } = (location as any)?.query || {};
  /** Agent 路由下无 Copilot 停靠逻辑，跳过左侧菜单自动展开/收起 */
  const isAgentPage = pathname?.split('/')[1] === 'agent';
  const { formatMessage, locale } = useIntl();
  const value = storage.getItem('__dba_show_left_menu');
  // 全局会话模式状态（供 Copilot 等无 URL template 的场景消费）
  const [sessionTemplateMode] = useLocalState<string>(
    '__dba_session_template_mode__',
    '',
  );
  // 当前会话是否已生成 COS 文件（output_metadata.cos_list 有值）→ 禁用返回 Copilot 按钮
  // manual: true 仅订阅缓存，不重复请求 /get_chat_list
  const { chatList, bookMarkedChats } = useChatList({ manual: true });
  const isAiGalleryActive = useMemo(() => {
    if (!sessionId) return false;
    const current = concat(chatList, bookMarkedChats).find(
      (item) => item?.session_id === sessionId,
    );
    return !!current?.output_metadata?.cos_list?.length;
  }, [sessionId, chatList, bookMarkedChats]);
  // 判断是否为智能表格/魔法海报/AI Gallery 模式（禁用返回 Copilot 按钮）
  // 1. 聊天页面：通过 URL template，附带sessionTemplateMode支持参数判断
  // 3. Home 页面：通过 URL tab 参数判断（新建和历史两种场景）
  // 4. COS 返回 ai_gallery_file 标签时，禁用返回 Copilot 按钮
  const isExcelReport =
    ['aiExcel', 'magicPoster', 'splitView'].includes(template) ||
    ['aiExcel', 'magicPoster', 'splitView'].includes(sessionTemplateMode) ||
    ['aiExcel', 'magicPoster'].includes(tab) ||
    isAiGalleryActive;
  const copilotTextBack = formatMessage({ id: 'dba_back_to_copilot' });
  const { setItem } = useAgentStoreContext();
  const [, setIsShowCopilot] = useLocalState('__dba_show_copilot__', false);
  const [, setNewChatClickAt] = useLocalState<number>(
    '__dba_home_new_chat_click_at__',
    0,
  );
  const { activeReport } = useGlobalReportManager({ sessionId });
  const showErrorTips = useErrorTips();

  // 获取 openCopilotWithAgentId 用于设置 Agent（不打开 Copilot）
  const { openCopilotWithAgentId } = useCopilotOpenBridge();
  // 清除全局会话模式状态，防止 skill 模式残留影响新对话
  const [, setSessionTemplateMode] = useLocalState<string>(
    '__dba_session_template_mode__',
    '',
  );

  const handleClickNewChat = () => {
    // 通知 Home：用户主动点击了"新增对话"
    setNewChatClickAt(Date.now());
    // 清除 skill 等模板模式状态，回到正常 agent 模式
    setSessionTemplateMode('');
    // 默认选中会话列表最新会话最后一轮的 Agent，无记录时回退 DataBrain
    const firstSessionId = chatList?.[0]?.session_id;
    const conversations = firstSessionId
      ? store.getItem<any[]>(`__dba_chat_history_${firstSessionId}__`, []) ?? []
      : [];
    // Skill 会话的 modeId 为 Skill 专用 UUID，无法作为 Agent 选中态，回落 DataBrain 2.0
    const modeId = last(conversations)?.modeId;
    const latestAgentId =
      modeId && modeId !== SKILL_AGENT_UUID ? modeId : DATABRAIN_AGENT_ID2;
    openCopilotWithAgentId([latestAgentId]);
    history.push('/agent/home');
  };

  useEffect(() => {
    storage.setItem('__dba_show_left_menu', isNormal ? 'show' : 'hidden');
  }, [isNormal]);

  useEffect(() => {
    setItem('__dba_show_left_menu__', onSet);
    // Agent 页无 Copilot 停靠，不根据本地缓存自动展开/收起左侧菜单
    if (isAgentPage) return;
    if (value === 'show') onSet(false);
    else onSet(true);
  }, []);

  /**
   * 按关键词搜索会话列表；空值则重置搜索态（对齐 HistoryChatList）。
   * @param keyword - 搜索关键词
   */
  const handleOnSearch = async (keyword?: string): Promise<void> => {
    if (!keyword?.trim()) {
      setSearchChatData(undefined);
      setShowNoDataInReturn(false);
      return;
    }

    const data = await getSearchSessionQuery(
      { trace_id: uuid(), query: keyword.trim() },
      () => showErrorTips,
    );

    if (data.length > 0) {
      setSearchChatData(data);
      setShowNoDataInReturn(false);
      return;
    }

    setSearchChatData(undefined);
    setShowNoDataInReturn(true);
  };

  /**
   * 输入防抖搜索（500ms）。
   * @param keyword - SearchBox onChange 当前值
   */
  const { run: debouncedSearch, cancel: cancelDebouncedSearch } = useDebounceFn(
    (keyword?: string): void => {
      void handleOnSearch(keyword);
    },
    { wait: 500 },
  );

  /**
   * 清空搜索框并重置搜索态。
   */
  const handleSearchClear = (): void => {
    cancelDebouncedSearch();
    void handleOnSearch();
  };

  const handleBackToCopilot = () => {
    // 如果报告面板已展开，先触发保存
    if (activeReport?.show) {
      setItem('__dba_trigger_report_save__', true);
    }
    // 判断依据统一为 activeReport?.show（当前 session 是否真的有报告在展示），
    // 而不是 URL 上的 template 参数：template 在普通聊天模式下生成 AI Gallery
    // 文件时并不存在，仅依赖 template 会误将刚自动打开的报告 session 绑定清空。
    // isExcelReport 作为兜底（用户手动关闭报告面板后，仍处于 aiExcel/magicPoster/
    // AI Gallery 会话，也应保留绑定）。
    if (activeReport?.show || isExcelReport) {
      // 报告已展开/处于特殊会话模式：保留报告 session 绑定，通知 ChatDSTemplate
      // 卸载时跳过清除逻辑，让 CopilotReportWindow 能读到报告并自动展示
      store.setItem('__dba_to_copilot__', true);
      store.setItem(COPILOT_REPORT_SESSION_ID_KEY, sessionId);
    } else {
      // 无报告展开且非特殊模式，清空绑定，防止 Copilot 打开时误自动打开旧报告
      store.setItem(COPILOT_REPORT_SESSION_ID_KEY, '');
    }
    setItem('__dba_chat_copilot__', sessionId);
    setIsShowCopilot(true);
    history.push({
      pathname: '/home',
    });
  };

  return (
    <>
      {isNormal ? (
        <>
          <div className="dba-agent-sidebar-header">
            <div className={'dba-leftmenu-title'}>
              <div>
                <img src={DataBrainLogo} alt="Databrain Logo" />
                <OrganizationText
                  textKey="agentTitle"
                  className="dba-logo-text"
                />
              </div>

              <CommonBubble
                closeOnScroll
                destroyOnClose
                content={copilotTextBack}
                placement="top"
              >
                <img
                  style={{ cursor: 'pointer' }}
                  src={CopilotBackColor}
                  alt="Copilot Back Button"
                  onClick={handleBackToCopilot}
                />
              </CommonBubble>
            </div>

            <div
              className="dba-newQa"
              data-locale={locale}
              onClick={handleClickNewChat}
            >
              <SvgIcon type="add-icon-type" className="dba-plus"></SvgIcon>
              <div>{formatMessage({ id: 'dba_new_qa' })}</div>
              <SvgIcon type="ai-agent" className="dba-icon"></SvgIcon>
            </div>

            {/* <OrganizationPermission fallback={<div style={{ margin: '15px 0px'}}/>}> */}
            <RouterNavLink
              className="dba-new-collection"
              to="/agent/collection"
            >
              <img
                src={AgentCollectionDefaultIcon}
                alt="Agent Collection"
                className="dba-collection-icon dba-collection-icon--default"
              />
              <img
                src={AgentCollectionIcon}
                alt="Agent Collection"
                className="dba-collection-icon dba-collection-icon--hover"
              />
              <div className="dba-new-collection__text">
                {formatMessage({ id: 'dba_collection_title' })}
              </div>
            </RouterNavLink>

            {/* </OrganizationPermission>  */}

            <RouterNavLink className="dba-new-skills" to="/agent/skills">
              <img
                src={SkillsCollectionDefaultIcon}
                alt="Skills Collection"
                className="dba-skills-icon dba-skills-icon--default"
              />
              <img
                src={SkillsCollectionIcon}
                alt="Skills Collection"
                className="dba-skills-icon dba-skills-icon--hover"
              />
              <div className="dba-new-skills__text">
                {formatMessage({ id: 'dba_skills_collection_title' })}
              </div>
            </RouterNavLink>

            <SearchBox
              className={'dba-search-box'}
              placeholder={formatMessage({ id: 'dba_search_chats' })}
              onChange={debouncedSearch}
              onClear={handleSearchClear}
            />
          </div>
        </>
      ) : (
        <div className="dba-menu-small">
          <div
            className="dba-small-icon dba-small-icon--primary"
            onClick={handleClickNewChat}
          >
            <BasicImage src={ChatNewIcon} />
          </div>
          <div
            className="dba-small-icon"
            onClick={() => {
              onSet(false);
              setInitialTransitionDisabled(false);
            }}
          >
            <BasicImage src={ArrowIcon} className="dba-small-icon-arrow" />
          </div>
          <Bubble
            dark
            content={
              <>
                <span>{formatMessage({ id: 'dba_source_tip' })}</span>
              </>
            }
            placement="right"
          >
            <div className="dba-small-icon">
              <BasicImage src={InfoIcon} />
            </div>
          </Bubble>
          <Bubble dark content={copilotTextBack} placement="right">
            <div className="dba-small-icon" onClick={handleBackToCopilot}>
              <BasicImage
                src={ExtractIcon}
                className={isExcelReport ? 'dba-copilot-back-btn-disabled' : ''}
              />
            </div>
          </Bubble>
        </div>
      )}
    </>
  );
};

export default MainMenu;
